/**
 * New-user modal — pick org → fields update to show only the roles
 * legal under that org's type.
 */

import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { useOrganizations } from '@/hooks/use-organizations.js';
import { useCreateUser } from '@/hooks/use-users.js';

interface Props {
  open: boolean;
  onClose: () => void;
}

const STAFF_ROLES = ['ops', 'staff'] as const;
const CLIENT_ROLES = ['client_admin', 'client_submitter', 'client_viewer'] as const;

export function NewUserDialog({ open, onClose }: Props) {
  const orgsQuery = useOrganizations();
  const create = useCreateUser();

  const [organizationId, setOrganizationId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('');

  const orgs = orgsQuery.data ?? [];
  const selectedOrg = orgs.find((o) => o.id === organizationId);

  const allowedRoles = useMemo(() => {
    if (!selectedOrg) return [] as ReadonlyArray<string>;
    return selectedOrg.type === 'internal' ? STAFF_ROLES : CLIENT_ROLES;
  }, [selectedOrg]);

  useEffect(() => {
    if (open) {
      setOrganizationId('');
      setName('');
      setEmail('');
      setRole('');
    }
  }, [open]);

  // When org changes, reset role to a sensible default for the audience.
  useEffect(() => {
    if (allowedRoles.length === 0) {
      setRole('');
      return;
    }
    if (!allowedRoles.includes(role)) {
      setRole(allowedRoles[0] as string);
    }
  }, [allowedRoles, role]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !create.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, create.isPending, onClose]);

  if (!open) return null;

  function handleSubmit() {
    if (!organizationId) return toast.error('Pick an organisation');
    if (!name.trim()) return toast.error('Name is required');
    if (!email.trim()) return toast.error('Email is required');
    if (!role) return toast.error('Pick a role');
    create.mutate(
      {
        organizationId,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role: role as
          | 'ops'
          | 'staff'
          | 'client_admin'
          | 'client_submitter'
          | 'client_viewer',
      },
      {
        onSuccess: () => {
          toast.success('User created');
          onClose();
        },
      }
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dialog-in-backdrop"
      onClick={create.isPending ? undefined : onClose}
    >
      <div
        className="w-full max-w-md mx-4 rounded-lg border border-ink-600 bg-ink-850 shadow-xl dialog-in-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-ink-700">
          <h2 className="text-[15px] font-semibold text-ink-100">
            New user
          </h2>
          <button
            onClick={onClose}
            disabled={create.isPending}
            className="text-ink-300 hover:text-ink-100 focus-ring rounded-md p-1 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-3">
          <Field label="Organisation *">
            <select
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              disabled={create.isPending || orgsQuery.isLoading}
              className="h-8 px-2.5 text-[13px] bg-ink-800 border border-ink-600 rounded-md w-full focus-ring"
            >
              <option value="">— select —</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} · {o.type}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Name *">
            <Input
              type="text"
              value={name}
              disabled={create.isPending}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field label="Email *">
            <Input
              type="email"
              value={email}
              disabled={create.isPending}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <Field label="Role *">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={create.isPending || allowedRoles.length === 0}
              className="h-8 px-2.5 text-[13px] bg-ink-800 border border-ink-600 rounded-md w-full focus-ring"
            >
              {allowedRoles.length === 0 && (
                <option value="">Pick an organisation first</option>
              )}
              {allowedRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-ink-700">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create user'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
